import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient, useProvisioningClient } from "@/lib/api";
import {
  ProjectListResponse,
  ChecklistResponse,
  CreateProjectInput,
  UpdateProjectInput,
  Project,
  ProvisionSlackChannelsResponse,
  IntegrationAction,
  IntegrationActionResponse,
  IntegrationSwapResponse,
  ProvisioningTool,
} from "@/types";
import type { SyncAccessCounts } from "@/lib/sync-access-summary";

/**
 * Reserved `status` value meaning "no status filter — every real status, Closed included".
 *
 * ── WHY THE HOOK SENDS A VALUE WHERE IT USED TO SEND NOTHING ───────────────────────────────────
 * Omitting `status` is NOT neutral on this endpoint: `list-projects.ts` reads an absent param as
 * its legacy default view and applies `status NOT IN ('Closed', 'TEMPLATE')`. So the Projects
 * toolbar's "All statuses" option, which sent no param, actually meant "all except Closed" — the
 * label was false, and a Closed project was unreachable unless you knew to pick Closed explicitly.
 *
 * Sending `status=all` is the explicit signal that distinguishes "I want everything" from "I have
 * no opinion". `TEMPLATE` stays excluded server-side in every branch — `__template__` is a system
 * sentinel row, never a real project.
 */
export const ALL_STATUSES = "all";

export function useProjects(filters: {
  status?: string;
  phase?: string;
  search?: string;
  limit?: number;
  cursor?: string;
}) {
  const client = useApiClient();

  return useQuery({
    queryKey: ["projects", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      // Always explicit — an empty/absent filter means "all statuses", and that has to be said
      // out loud or the server applies its Closed-hiding default instead. See ALL_STATUSES.
      params.append(
        "status",
        filters.status?.trim() ? filters.status.trim() : ALL_STATUSES,
      );
      if (filters.phase) params.append("phase", filters.phase);
      if (filters.search) params.append("search", filters.search);
      if (filters.limit) params.append("limit", filters.limit.toString());
      if (filters.cursor) params.append("cursor", filters.cursor);

      const response = await client.get<ProjectListResponse>(
        `/api/projects?${params.toString()}`,
      );
      const data = response.data;

      // Oscar intakes (source='oscar', status='pending_approval') live in the Intakes tab, not
      // the Projects tab. Filter them out so they never appear in the main project list.
      if (data?.projects) {
        data.projects = data.projects.filter(
          (p: { source?: string; status?: string | null }) =>
            !(p.source === "oscar" && p.status === "pending_approval"),
        );
      }

      return data;
    },
  });
}

export function useProject(projectId: string) {
  const client = useApiClient();

  return useQuery({
    queryKey: ["project", projectId],
    queryFn: async () => {
      const response = await client.get(`/api/projects/${projectId}`);
      return response.data;
    },
    enabled: !!projectId,
  });
}

export function useProjectChecklist(projectId: string) {
  const client = useApiClient();

  return useQuery({
    queryKey: ["checklist", projectId],
    queryFn: async () => {
      const response = await client.get<ChecklistResponse>(
        `/api/projects/${projectId}/checklist`,
      );
      return response.data;
    },
    enabled: !!projectId,
  });
}

export function useCreateProject() {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateProjectInput) => {
      const response = await client.post("/api/projects", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

/**
 * PATCH /api/projects/{projectId} — metadata + linkage edits.
 * Linkage fields (github_repo/github_url/slack_*_channel_id) are admin/leadership-only;
 * the server re-enforces this on the Cognito group claim (projects-architecture §12.1).
 */
export function useUpdateProject(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateProjectInput) => {
      const response = await client.patch<Project>(
        `/api/projects/${projectId}`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

/**
 * POST /api/projects/{projectId}/slack/provision — resolve/create the micro + macro
 * Slack channels (CR-05, FR-P2-039) and persist the returned non-secret channel ids.
 * The workspace bot token never crosses this boundary — response carries channel ids only.
 */
export function useProvisionSlack(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await client.post<ProvisionSlackChannelsResponse>(
        `/api/projects/${projectId}/slack/provision`,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    },
  });
}

/** Body for POST /api/projects/{projectId}/approve — assign the team + confirm type/hours. */
export interface ApproveProjectInput {
  title?: string;
  solution_architect: string;
  project_manager: string;
  engineers_assigned?: string;
  project_type?: string;
  sow_hours?: number;
}

/**
 * POST /api/projects/{projectId}/approve — leadership/admin approves a pending_approval project.
 * Fires provisioning + template seeding server-side. Invalidates the project lists on success.
 */
export function useApproveProject(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ApproveProjectInput) => {
      const response = await client.post(
        `/api/projects/${projectId}/approve`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

/**
 * POST /api/projects/{projectId}/reject — leadership/admin rejects a pending_approval project.
 * Posts a Slack notification server-side. Invalidates the project lists on success.
 */
export function useRejectProject(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { reason: string }) => {
      const response = await client.post(
        `/api/projects/${projectId}/reject`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useImportJira() {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      jira_base_url: string;
      project_key: string;
    }) => {
      const response = await client.post("/api/projects/import-jira", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

/** Variables for {@link useIntegrationAction}. */
export interface IntegrationActionInput {
  tool: ProvisioningTool;
  action: IntegrationAction;
  /** Jira retry only — a replacement project key after "key already in use". */
  jiraProjectKey?: string;
}

/**
 * POST /api/projects/{projectId}/integrations/{tool}/{action} — "Cancel auto-setup" (pending /
 * provisioning → cancelled) or "Retry auto-setup" (failed / cancelled / skipped → re-run).
 * Served by the separate Provisioning API (see useProvisioningClient). A 409
 * INVALID_PROVISIONING_STATE means the state moved underneath the user; the project query is
 * invalidated on success AND on error so the card re-renders the real state either way.
 */
export function useIntegrationAction(projectId: string) {
  const client = useProvisioningClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tool,
      action,
      jiraProjectKey,
    }: IntegrationActionInput) => {
      const response = await client.post<IntegrationActionResponse>(
        `/api/projects/${encodeURIComponent(projectId)}/integrations/${tool}/${action}`,
        jiraProjectKey ? { jira_project_key: jiraProjectKey } : undefined,
      );
      return response.data;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

/** Variables for {@link useIntegrationSwap}. */
export interface IntegrationSwapInput {
  tool: ProvisioningTool;
  /** The replacement: repo / channel / Jira key or URL / Clockify id or URL / SharePoint site or folder URL. */
  value: string;
  /** true → validate and return before/after without writing. */
  preview: boolean;
}

/**
 * POST /api/projects/{projectId}/integrations/{tool}/swap — re-point an ACTIVE integration.
 * Call with preview=true first to show the user both resources, then preview=false to apply.
 * Only the applying call invalidates the project query.
 */
export function useIntegrationSwap(projectId: string) {
  const client = useProvisioningClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tool, value, preview }: IntegrationSwapInput) => {
      const response = await client.post<IntegrationSwapResponse>(
        `/api/projects/${encodeURIComponent(projectId)}/integrations/${tool}/swap`,
        { value, preview },
      );
      return response.data;
    },
    onSuccess: (data) => {
      if (!data.applied) return;
      void queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

/** Response of POST /api/projects/{projectId}/resources/sync-access — counts only, never identities. */
export interface SyncResourceAccessResult {
  project_id: string;
  /** Per-integration ok/failed/skipped counters (mirrors backend `AccessCounts`). */
  counts: SyncAccessCounts;
  /**
   * Active, email-bearing resources the run considered. null when the server could not count;
   * absent on an older server. Distinguishes "no team members" from "nothing linked".
   */
  resource_count?: number | null;
}

/**
 * POST /api/projects/{projectId}/resources/sync-access — re-apply every assigned resource's access
 * to Slack / Jira / GitHub / SharePoint. Idempotent server-side. (Assignments trigger this
 * automatically since Stream A; the button covers removals, identity fixes and outages.)
 */
export function useSyncResourceAccess(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await client.post<SyncResourceAccessResult>(
        `/api/projects/${encodeURIComponent(projectId)}/resources/sync-access`,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    },
  });
}

/** Response of GET /api/jira/validate-key. */
export interface ValidateJiraKeyResult {
  available: boolean;
  /** Normalised key — exactly what provisioning will create (e.g. "cst-667" → "CST667"). */
  key: string;
  checked: boolean;
  reason?: string;
  message?: string;
}

/**
 * Imperative Jira-key availability check (GET /api/jira/validate-key) for submit-time validation.
 * Returns null when the check itself could not run (network / 5xx) so callers can let the user
 * proceed — the provisioner still refuses to link a key that turns out to be taken.
 */
export function useValidateJiraKey() {
  const client = useApiClient();
  return async (key: string): Promise<ValidateJiraKeyResult | null> => {
    try {
      const response = await client.get<ValidateJiraKeyResult>(
        "/api/jira/validate-key",
        {
          params: { key },
        },
      );
      return response.data;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 400) {
        return {
          available: false,
          key,
          checked: true,
          message:
            "Jira project key must be 2–10 letters or digits and start with a letter.",
        };
      }
      return null;
    }
  };
}
